import React, { useState, useEffect, useRef, useMemo } from 'react';
import { supabase } from '../lib/supabaseClient';
import {
  CLINICAL_SEVERITY_LEVELS,
  NON_CLINICAL_SEVERITY_LEVELS,
  DENTAL_RISK_CATEGORIES,
  INCIDENT_CRITERIA_PRESETS,
  COMPREHENSIVE_DENTAL_INCIDENTS,
  getLocalDateTimeString
} from '../lib/constants';
import DentalRiskUploadZone from './DentalRiskUploadZone';
import CameraScanner from './CameraScanner';
import { SeverityGuideModal } from './risk/SeverityGuideModal';

const STORAGE_CACHE_KEY = 'DENTAL_RISK_REPORTS_CACHE_V1';

const DEFAULT_LOCATIONS = [
  'ไม่ระบุ',
  'ห้องจ่ายกลาง (CSSD)',
  'คลีนิกห้อง 1',
  'คลีนิกห้อง 2',
  'คลีนิกห้อง 3',
  'คลีนิกห้อง 4',
  'คลีนิกห้อง 5',
  'ห้อง X-Ray / CT',
  'ห้องตรวจประเมิน / คัดกรอง',
  'คลังพัสดุและเวชภัณฑ์ทันตกรรม',
  'จุดล้างและทำลายเชื้อ'
];

// 5 Most Frequent Quick Tags
const FREQUENT_QUICK_TAGS = [
  {
    chipLabel: '🔥 เข็มทิ่มตำ',
    title: 'เข็มตำ/ของมีคมบาด',
    domain: 'non_clinical',
    severity_level: '3',
    location: 'คลีนิกห้อง 1',
    category: 'ความปลอดภัยและสิ่งแวดล้อม (Safety & Environment)',
    sub_category: 'อุบัติเหตุของมีคม / เข็มตำบุคลากร (Needle Stick Injury)',
    is_patient_involved: false
  },
  {
    chipLabel: '🔥 ยาชาแพ้',
    title: 'ผู้ป่วยเป็นลม/วูบ (Vasovagal Syncope)',
    domain: 'clinical',
    severity_level: 'D',
    location: 'คลีนิกห้อง 1',
    category: 'ภาวะฉุกเฉินทางการแพทย์ในคลินิก',
    sub_category: 'ผู้ป่วยเป็นลมหมดสติ (Vasovagal Syncope)',
    is_patient_involved: false
  },
  {
    chipLabel: '🔥 ซองเปียก',
    title: 'ซองเปียกชื้น (Wet pack)',
    domain: 'cssd',
    severity_level: 'B',
    location: 'ห้องจ่ายกลาง (CSSD)',
    category: 'บรรจุภัณฑ์และการจัดเก็บ (Packaging & Storage)',
    sub_category: 'ซองเปียกชื้น (Wet pack / Moisture detected)',
    is_patient_involved: false
  },
  {
    chipLabel: '🔥 หัวกรอชำรุด',
    title: 'หัวกรอหัก/ด้ามกรอชำรุดขณะใช้งาน',
    domain: 'clinical',
    severity_level: 'D',
    location: 'คลีนิกห้อง 1',
    category: 'เครื่องมือและอุปกรณ์ทางการแพทย์',
    sub_category: 'หัวกรอ / ด้ามกรอแตกหักชำรุด',
    is_patient_involved: false
  },
  {
    chipLabel: '🔥 รากฟันหัก',
    title: 'ตะไบหักในคลองรากฟัน (Broken file)',
    domain: 'clinical',
    severity_level: 'E',
    location: 'คลีนิกห้อง 1',
    category: 'ภาวะแทรกซ้อนจากการรักษาทางทันตกรรม',
    sub_category: 'ตะไบหักในคลองรากฟัน (Broken file)',
    is_patient_involved: false
  }
];

/**
 * RiskQuickReportForm
 * ฟอร์มรายงานอุบัติการณ์และความเสี่ยงทันตกรรมและงานจ่ายกลางแบบเร่งด่วนสำหรับมือถือในหน้าเดียว
 */
const RiskQuickReportForm = ({
  onSuccess = () => {},
  onCancel = null,
  user = null,
  initialExpanded = false,
  onSwitchToGuided = null
}) => {
  // Form State
  const [domain, setDomain] = useState('clinical'); // 'clinical' | 'cssd' | 'non_clinical'
  const [severityLevel, setSeverityLevel] = useState('C');
  const [title, setTitle] = useState('');
  const [incidentDate, setIncidentDate] = useState(() => getLocalDateTimeString());
  const [location, setLocation] = useState('ไม่ระบุ');
  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [description, setDescription] = useState('');
  const [instrumentBarcode, setInstrumentBarcode] = useState('');
  const [sterileCycleId, setSterileCycleId] = useState('');
  const [machineId, setMachineId] = useState('');
  const [images, setImages] = useState([]);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [reporterName, setReporterName] = useState(user?.name || user?.email?.split('@')[0] || '');
  const [reporterRole, setReporterRole] = useState('ทันตแพทย์');
  const [contactInfo, setContactInfo] = useState(user?.email || '');
  const [patientHn, setPatientHn] = useState('');
  const [isPatientInvolved, setIsPatientInvolved] = useState(false);

  // Progressive Disclosure Accordion State
  const [isAccordionOpen, setIsAccordionOpen] = useState(initialExpanded);

  // UI / Action State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [showSeverityGuide, setShowSeverityGuide] = useState(false);
  const [isLookingUpBarcode, setIsLookingUpBarcode] = useState(false);
  const [barcodeLookupNote, setBarcodeLookupNote] = useState(null);

  // Smart Search Suggestions Dropdown State
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputContainerRef = useRef(null);

  // Web Speech API Voice Recognition
  const [isListening, setIsListening] = useState(false);
  const [activeSpeechField, setActiveSpeechField] = useState('title'); // 'title' | 'description'
  const [speechSupported, setSpeechSupported] = useState(true);
  const recognitionRef = useRef(null);

  // ตรวจสอบ Web Speech API
  useEffect(() => {
    const SpeechRecognition = typeof window !== 'undefined'
      ? (window.SpeechRecognition || window.webkitSpeechRecognition)
      : null;

    if (!SpeechRecognition) {
      setSpeechSupported(false);
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'th-TH';

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          transcript += event.results[i][0].transcript;
        }
        if (activeSpeechField === 'title') {
          setTitle((prev) => (prev ? `${prev} ${transcript}`.trim() : transcript));
        } else {
          setDescription((prev) => (prev ? `${prev} ${transcript}`.trim() : transcript));
        }
      };

      recognition.onerror = (err) => {
        console.warn('[SpeechRecognition] error:', err);
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    } catch (e) {
      console.warn('SpeechRecognition initialization error:', e);
      setSpeechSupported(false);
    }
  }, [activeSpeechField]);

  // สลับการบันทึกเสียง
  const toggleSpeechRecognition = (field = 'title') => {
    setActiveSpeechField(field);
    if (!recognitionRef.current) return;

    if (isListening) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
      } catch (e) {
        console.warn('Could not start recognition:', e);
      }
    }
  };

  const isPresetRef = useRef(false);

  // ปรับ Category และ Severity เริ่มต้นเมื่อเปลี่ยน Domain
  useEffect(() => {
    if (isPresetRef.current) {
      isPresetRef.current = false;
      return;
    }

    const cats = DENTAL_RISK_CATEGORIES[domain] || [];
    if (cats.length > 0) {
      setCategory(cats[0].category);
      setSubCategory(cats[0].subCategories ? cats[0].subCategories[0] : '');
    } else {
      setCategory('');
      setSubCategory('');
    }

    if (domain === 'cssd') {
      setLocation('ห้องจ่ายกลาง (CSSD)');
      setSeverityLevel('B');
      setIsPatientInvolved(false);
    } else if (domain === 'clinical') {
      setLocation((prevLoc) => (prevLoc === 'ห้องจ่ายกลาง (CSSD)' ? 'คลีนิกห้อง 1' : prevLoc));
      setSeverityLevel('C');
      setIsPatientInvolved(false);
    } else {
      setSeverityLevel('2');
      setIsPatientInvolved(false);
    }
  }, [domain]);

  // เลือกเหตุการณ์จากแท็กด่วน หรือจากคำค้นหา
  const handleSelectIncident = (incident) => {
    if (!incident) return;
    const tagTitle = incident.tag || incident.title;
    setTitle(tagTitle);
    setSubmitError('');
    setShowSuggestions(false);

    const preset = incident.domain ? incident : (INCIDENT_CRITERIA_PRESETS[tagTitle] || {});
    isPresetRef.current = true;
    if (preset.domain) setDomain(preset.domain);
    if (preset.severity_level) setSeverityLevel(preset.severity_level);
    if (preset.category) setCategory(preset.category);
    if (preset.sub_category) setSubCategory(preset.sub_category);
    if (preset.location) setLocation(preset.location);
    if (typeof preset.is_patient_involved === 'boolean') {
      setIsPatientInvolved(preset.is_patient_involved);
    }
  };

  // Smart suggestions matching user query
  const filteredSuggestions = useMemo(() => {
    const q = title.trim().toLowerCase();
    if (!q || q.length < 2) return [];
    return COMPREHENSIVE_DENTAL_INCIDENTS.filter((item) => {
      if (item.tag.toLowerCase().includes(q)) return true;
      if (item.keywords && item.keywords.some((k) => k.toLowerCase().includes(q))) return true;
      if (item.sub_category && item.sub_category.toLowerCase().includes(q)) return true;
      return false;
    }).slice(0, 4);
  }, [title]);

  // ค้นหา Barcode อัตโนมัติจาก Supabase
  const handleBarcodeScanned = async (scannedCode) => {
    if (!scannedCode) return;
    const code = scannedCode.trim();
    setInstrumentBarcode(code);
    setShowScanner(false);
    setIsLookingUpBarcode(true);
    setBarcodeLookupNote(null);

    try {
      if (supabase) {
        // 1. ค้นหาใน instruments
        const { data: instData } = await supabase
          .from('instruments')
          .select('*')
          .eq('unique_barcode', code)
          .maybeSingle();

        if (instData) {
          const name = instData.item_name || 'เครื่องมือทันตกรรม';
          setBarcodeLookupNote({
            type: 'success',
            text: `ตรวจพบ: ${name} (${instData.location || 'CSSD'})`
          });
          setTitle((prev) => (prev ? prev : `[เครื่องมือ] ${name} - พบปัญหา`));
          if (instData.location) {
            setLocation(instData.location.replace(/^[\s-]+/, '').trim());
          }
          if (instData.cycle || instData.details?.cycle) {
            setSterileCycleId(instData.cycle || instData.details?.cycle);
          }
          if (instData.machine) {
            setMachineId(instData.machine);
          }
          setDomain('cssd');
          return;
        }

        // 2. ค้นหาใน item_master
        const { data: itemData } = await supabase
          .from('item_master')
          .select('*')
          .or(`unique_barcode.eq.${code},barcode.eq.${code}`)
          .maybeSingle();

        if (itemData) {
          const itemName = itemData.item_name || itemData.name || 'พัสดุ/เครื่องมือ';
          setBarcodeLookupNote({
            type: 'success',
            text: `ตรวจพบ: ${itemName}`
          });
          setTitle((prev) => (prev ? prev : `[พัสดุ] ${itemName} - ชำรุด/พบปัญหา`));
          return;
        }
      }

      setBarcodeLookupNote({
        type: 'info',
        text: `บันทึกรหัส: ${code} (ไม่พบข้อมูลอัตโนมัติ แต่บันทึกรหัสลงรายงานแล้ว)`
      });
    } catch (err) {
      console.warn('[BarcodeLookup] error:', err);
    } finally {
      setIsLookingUpBarcode(false);
    }
  };

  // คำนวณคะแนน Severity
  const getSeverityScore = () => {
    if (domain === 'non_clinical') {
      const found = NON_CLINICAL_SEVERITY_LEVELS.find((l) => l.code === severityLevel);
      return found?.score || 1;
    }
    const found = CLINICAL_SEVERITY_LEVELS.find((l) => l.code === severityLevel);
    return found?.score || 1;
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSubmitError('');

    if (!title.trim()) {
      setSubmitError('กรุณาระบุหัวข้ออุบัติการณ์ หรือเลือกจากแท็กพบบ่อย');
      return;
    }

    setIsSubmitting(true);

    const now = new Date();
    const yy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const randomSuffix = Math.floor(100 + Math.random() * 900);
    const incidentNumber = `D-RSK-${yy}${mm}-${randomSuffix}`;
    const finalIncidentDate = incidentDate
      ? new Date(incidentDate).toISOString()
      : now.toISOString();

    const newReport = {
      incident_number: incidentNumber,
      incident_date: finalIncidentDate,
      location: location || 'คลีนิกทันตกรรม',
      risk_domain: domain,
      category: category || 'ทั่วไป',
      sub_category: subCategory || '',
      severity_level: severityLevel,
      severity_score: getSeverityScore(),
      title: title.trim(),
      description: description.trim() || title.trim(),
      immediate_action: '',
      contributing_factors: '',
      is_patient_involved: isPatientInvolved,
      patient_hn: isPatientInvolved ? patientHn : '',
      procedure_name: '',
      instrument_barcode: instrumentBarcode || null,
      sterile_cycle_id: sterileCycleId || null,
      machine_id: machineId || null,
      image_urls: images,
      audio_note_url: null,
      is_anonymous: isAnonymous,
      reporter_name: isAnonymous ? 'นิรนาม (Anonymous)' : reporterName,
      reporter_role: isAnonymous ? '-' : reporterRole,
      contact_info: isAnonymous ? '-' : contactInfo,
      status: 'pending',
      investigator_name: '',
      root_cause_analysis: '',
      rca_fishbone_category: null,
      corrective_action: '',
      recall_batch_triggered: false,
      created_at: now.toISOString(),
      last_updated: now.toISOString()
    };

    let savedRecord = newReport;

    // 1. Supabase insert
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('dental_risk_reports')
          .insert([newReport])
          .select();

        if (!error && data && data.length > 0) {
          savedRecord = data[0];
        } else {
          console.warn('[RiskQuickReportForm] Supabase insert warning:', error?.message);
        }
      } catch (err) {
        console.warn('[RiskQuickReportForm] Supabase error, falling back to cache:', err);
      }
    }

    // 2. Sync LocalStorage Cache
    try {
      const cached = localStorage.getItem(STORAGE_CACHE_KEY);
      const parsed = cached ? JSON.parse(cached) : [];
      const updated = [savedRecord, ...parsed.filter((r) => r.incident_number !== savedRecord.incident_number)];
      localStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify(updated));
    } catch (cErr) {
      console.warn('Failed to update local cache:', cErr);
    }

    setIsSubmitting(false);
    onSuccess(savedRecord);
  };

  // Severity list based on Domain
  const severityLevelsList =
    domain === 'non_clinical' ? NON_CLINICAL_SEVERITY_LEVELS : CLINICAL_SEVERITY_LEVELS;

  const activeSev = severityLevelsList.find((s) => s.code === severityLevel);

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
        border: '1.5px solid #e2e8f0',
        padding: '12px 14px',
        maxWidth: '560px',
        margin: '0 auto',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}
    >
      {/* Top Header Bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px',
          paddingBottom: '8px',
          borderBottom: '1px solid #f1f5f9',
          gap: '6px',
          flexWrap: 'wrap'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '1.05em', fontWeight: '900', color: '#0f172a', letterSpacing: '-0.01em' }}>
            ⚡ รายงานด่วน
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button
            type="button"
            aria-label="เกณฑ์ความรุนแรง & RCA"
            onClick={() => setShowSeverityGuide(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '3px 8px',
              borderRadius: '6px',
              backgroundColor: '#eff6ff',
              color: '#1d4ed8',
              border: '1px solid #bfdbfe',
              fontSize: '0.74em',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            <span>ℹ️</span>
            <span>เกณฑ์ RCA</span>
          </button>

          {onSwitchToGuided && (
            <button
              type="button"
              onClick={onSwitchToGuided}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '3px 8px',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                color: '#0284c7',
                border: '1px solid #cbd5e1',
                fontSize: '0.74em',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f9ff';
                e.currentTarget.style.borderColor = '#0284c7';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
            >
              <span>🌟</span>
              <span>รายงานทีละขั้นตอน</span>
            </button>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {/* Field 1: Search / Title Input (Compact) */}
        <div ref={searchInputContainerRef} style={{ position: 'relative' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <input
              id="quick-incident-title"
              type="text"
              required
              placeholder="หัวข้ออุบัติการณ์... เช่น เข็มตำ, ยาชาแพ้"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setShowSuggestions(true);
                if (submitError) setSubmitError('');
              }}
              onFocus={() => setShowSuggestions(true)}
              style={{
                width: '100%',
                padding: '8px 36px 8px 10px',
                borderRadius: '8px',
                border: submitError ? '1.5px solid #ef4444' : '1px solid #cbd5e1',
                fontSize: '0.86em',
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
                outline: 'none'
              }}
            />

            {speechSupported && (
              <button
                type="button"
                onClick={() => toggleSpeechRecognition('title')}
                title={isListening && activeSpeechField === 'title' ? 'แตะเพื่อหยุดฟัง' : 'แตะเพื่อพูดด้วยเสียง'}
                style={{
                  position: 'absolute',
                  right: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '28px',
                  borderRadius: '6px',
                  backgroundColor: isListening && activeSpeechField === 'title' ? '#fee2e2' : '#f1f5f9',
                  border: isListening && activeSpeechField === 'title' ? '1px solid #ef4444' : '1px solid #cbd5e1',
                  color: isListening && activeSpeechField === 'title' ? '#dc2626' : '#64748b',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                🎙️
              </button>
            )}
          </div>

          {/* Quick Auto-Suggestions Popup */}
          {showSuggestions && filteredSuggestions.length > 0 && (
            <div
              style={{
                position: 'absolute',
                top: '40px',
                left: 0,
                right: 0,
                backgroundColor: '#ffffff',
                border: '1px solid #93c5fd',
                borderRadius: '8px',
                boxShadow: '0 6px 18px rgba(37, 99, 235, 0.15)',
                zIndex: 40,
                maxHeight: '140px',
                overflowY: 'auto',
                padding: '4px'
              }}
            >
              {filteredSuggestions.map((item) => (
                <div
                  key={item.id}
                  onMouseDown={() => handleSelectIncident(item)}
                  style={{
                    padding: '6px 8px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    fontSize: '0.78em',
                    color: '#1e293b'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <span style={{ fontWeight: '600' }}>
                    {item.domain === 'cssd' ? '🧼' : item.domain === 'clinical' ? '🩺' : '🏢'} {item.tag}
                  </span>
                  <span
                    style={{
                      fontSize: '0.72em',
                      padding: '1px 5px',
                      borderRadius: '4px',
                      backgroundColor: '#e0f2fe',
                      color: '#0369a1',
                      fontWeight: '700'
                    }}
                  >
                    Level {item.severity_level}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* 4-5 tiny pill chips beneath */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              overflowX: 'auto',
              gap: '4px',
              marginTop: '5px',
              paddingBottom: '2px',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {FREQUENT_QUICK_TAGS.map((tagItem, idx) => {
              const isSelected = title === tagItem.title || title === tagItem.chipLabel;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleSelectIncident(tagItem)}
                  style={{
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    padding: '3px 8px',
                    borderRadius: '12px',
                    border: isSelected ? '1.5px solid #0284c7' : '1px solid #cbd5e1',
                    backgroundColor: isSelected ? '#e0f2fe' : '#ffffff',
                    color: isSelected ? '#0369a1' : '#334155',
                    fontSize: '0.74em',
                    fontWeight: isSelected ? '700' : '500',
                    cursor: 'pointer',
                    boxShadow: isSelected ? '0 1px 3px rgba(2, 132, 199, 0.15)' : 'none'
                  }}
                >
                  {tagItem.chipLabel}
                </button>
              );
            })}
          </div>
        </div>

        {/* Field 2: Domain (ประเภทความเสี่ยง) - 3 compact capsules in 1 row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
          {[
            { id: 'clinical', label: '🩺 คลินิก', color: '#0284c7' },
            { id: 'cssd', label: '🧼 จ่ายกลาง', color: '#0d9488' },
            { id: 'non_clinical', label: '🏢 ทั่วไป', color: '#64748b' }
          ].map((item) => {
            const isSelected = domain === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setDomain(item.id)}
                style={{
                  padding: '6px 4px',
                  borderRadius: '8px',
                  border: isSelected ? '2px solid ' + item.color : '1px solid #cbd5e1',
                  backgroundColor: isSelected ? item.color + '14' : '#f8fafc',
                  color: isSelected ? item.color : '#475569',
                  fontWeight: isSelected ? '800' : '600',
                  fontSize: '0.82em',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.12s ease'
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {/* Field 3: Severity Level (ระดับความรุนแรง) */}
        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: domain === 'non_clinical' ? 'repeat(5, 1fr)' : 'repeat(9, 1fr)',
              gap: '4px'
            }}
          >
            {severityLevelsList.map((sev) => {
              const isSelected = severityLevel === sev.code;
              const harmLabel = sev.code === 'A' || sev.code === 'B'
                ? 'Near Miss'
                : sev.code === 'C' || sev.code === 'D'
                ? 'No Harm'
                : sev.code === 'E' || sev.code === 'F'
                ? 'Harm'
                : sev.code === 'G' || sev.code === 'H' || sev.code === 'I'
                ? 'Severe'
                : 'ระดับ ' + sev.code;

              return (
                <button
                  key={sev.code}
                  type="button"
                  aria-label={'Level ' + sev.code + ': ' + harmLabel}
                  onClick={() => setSeverityLevel(sev.code)}
                  style={{
                    height: '28px',
                    padding: 0,
                    borderRadius: '6px',
                    border: isSelected ? '2px solid ' + sev.color : '1px solid #e2e8f0',
                    backgroundColor: isSelected ? (sev.badgeBg || sev.color + '20') : '#f8fafc',
                    color: isSelected ? (sev.color || '#0f172a') : '#475569',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '0.84em',
                    fontWeight: '800',
                    boxShadow: isSelected ? '0 1px 4px ' + sev.color + '30' : 'none',
                    transition: 'all 0.12s ease'
                  }}
                >
                  {sev.code}
                </button>
              );
            })}
          </div>

          {/* 1-line concise pill when selected */}
          {activeSev && (
            <div
              style={{
                marginTop: '4px',
                padding: '3px 8px',
                borderRadius: '6px',
                backgroundColor: activeSev.badgeBg || '#f8fafc',
                border: '1px solid ' + (activeSev.badgeBorder || activeSev.color),
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '0.74em',
                color: activeSev.textColor || '#1e293b',
                lineHeight: '1.2'
              }}
            >
              <span
                style={{
                  backgroundColor: activeSev.color,
                  color: '#ffffff',
                  padding: '1px 5px',
                  borderRadius: '4px',
                  fontWeight: '800',
                  fontSize: '0.82em'
                }}
              >
                Level {activeSev.code}:
              </span>
              <span>รักษา: <strong>{activeSev.action_timeline || '-'}</strong></span>
              <span style={{ opacity: 0.3 }}>•</span>
              {activeSev.rca_required ? (
                <span style={{ color: '#dc2626', fontWeight: '700' }}>
                  ⚠️ ต้องทำ RCA ({activeSev.rca_timeline || 'ภายใน 7 วัน'})
                </span>
              ) : (
                <span style={{ color: '#16a34a', fontWeight: '700' }}>
                  🛡️ ไม่ต้องทำ RCA
                </span>
              )}
            </div>
          )}
        </div>

        {/* Field 4: When & Where (วันเวลา & สถานที่) - 2 columns */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
              <label htmlFor="quick-incident-date" style={{ fontWeight: '700', fontSize: '0.78em', color: '#334155' }}>
                วัน-เวลา *
              </label>
              <button
                type="button"
                onClick={() => setIncidentDate(getLocalDateTimeString(new Date()))}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontSize: '0.7em',
                  fontWeight: '700',
                  color: '#0284c7',
                  cursor: 'pointer'
                }}
              >
                🕒 ปัจจุบัน
              </button>
            </div>
            <input
              id="quick-incident-date"
              type="datetime-local"
              value={incidentDate}
              onChange={(e) => setIncidentDate(e.target.value)}
              style={{
                width: '100%',
                padding: '5px 6px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '0.78em',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff'
              }}
            />
          </div>

          <div>
            <label htmlFor="quick-incident-location" style={{ display: 'block', fontWeight: '700', fontSize: '0.78em', color: '#334155', marginBottom: '2px' }}>
              สถานที่เกิดเหตุ
            </label>
            <select
              id="quick-incident-location"
              aria-label="สถานที่เกิดเหตุ"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              style={{
                width: '100%',
                padding: '5px 6px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '0.78em',
                backgroundColor: '#ffffff',
                boxSizing: 'border-box'
              }}
            >
              {DEFAULT_LOCATIONS.map((loc, idx) => (
                <option key={idx} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Field 5: Optional Collapse Accordion */}
        <div>
          <button
            type="button"
            aria-label="ข้อมูลเพิ่มเติม"
            onClick={() => setIsAccordionOpen(!isAccordionOpen)}
            style={{
              width: '100%',
              padding: '6px 10px',
              borderRadius: isAccordionOpen ? '8px 8px 0 0' : '8px',
              border: '1px dashed #cbd5e1',
              backgroundColor: isAccordionOpen ? '#f1f5f9' : '#f8fafc',
              color: '#475569',
              fontSize: '0.78em',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'all 0.15s ease'
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <span>{isAccordionOpen ? '➖' : '➕'}</span>
              <span>{isAccordionOpen ? 'ปิดข้อมูลเพิ่มเติม' : 'ข้อมูลเพิ่มเติม: รูปถ่าย • บาร์โค้ด • ผู้ป่วย (เปิด/ปิด)'}</span>
            </span>
            <span style={{ fontSize: '0.85em' }}>{isAccordionOpen ? '▴' : '▾'}</span>
          </button>

          {isAccordionOpen && (
            <div
              style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                padding: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}
            >
              {/* Barcode / Scanner */}
              <div>
                <label style={{ display: 'block', fontWeight: '700', fontSize: '0.76em', color: '#334155', marginBottom: '4px' }}>
                  🏷️ บาร์โค้ดเครื่องมือ / รอบนึ่ง CSSD
                </label>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="พิมพ์รหัสบาร์โค้ด เช่น INS-2026-001"
                    value={instrumentBarcode}
                    onChange={(e) => setInstrumentBarcode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleBarcodeScanned(instrumentBarcode);
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '5px 8px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      fontSize: '0.78em',
                      backgroundColor: '#ffffff'
                    }}
                  />
                  {instrumentBarcode && (
                    <button
                      type="button"
                      onClick={() => handleBarcodeScanned(instrumentBarcode)}
                      disabled={isLookingUpBarcode}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: '#0f766e',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.76em',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {isLookingUpBarcode ? '...' : 'ค้นหา'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowScanner(true)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '5px 10px',
                      backgroundColor: '#0d9488',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: '700',
                      fontSize: '0.76em',
                      cursor: 'pointer'
                    }}
                  >
                    <span>📷</span>
                    <span>สแกน</span>
                  </button>
                </div>
                {barcodeLookupNote && (
                  <div
                    style={{
                      marginTop: '4px',
                      fontSize: '0.74em',
                      color: barcodeLookupNote.type === 'success' ? '#047857' : '#0369a1',
                      fontWeight: '600'
                    }}
                  >
                    {barcodeLookupNote.type === 'success' ? '✅' : 'ℹ️'} {barcodeLookupNote.text}
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                  <label style={{ fontWeight: '700', fontSize: '0.76em', color: '#334155' }}>
                    รายละเอียดเพิ่มเติม / การแก้ไขเบื้องต้น
                  </label>
                  {speechSupported && (
                    <button
                      type="button"
                      onClick={() => toggleSpeechRecognition('description')}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '2px',
                        padding: '1px 6px',
                        borderRadius: '10px',
                        backgroundColor: isListening && activeSpeechField === 'description' ? '#fee2e2' : '#ffffff',
                        color: isListening && activeSpeechField === 'description' ? '#dc2626' : '#64748b',
                        border: isListening && activeSpeechField === 'description' ? '1px solid #ef4444' : '1px solid #cbd5e1',
                        fontSize: '0.7em',
                        cursor: 'pointer'
                      }}
                    >
                      🎙️ พูด
                    </button>
                  )}
                </div>
                <textarea
                  rows={2}
                  placeholder="อธิบายสิ่งที่เกิดขึ้นเพิ่มเติม หรือการช่วยเหลือเฉพาะหน้า"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.78em',
                    boxSizing: 'border-box',
                    backgroundColor: '#ffffff'
                  }}
                />
              </div>

              {/* Photos */}
              <div>
                <label style={{ display: 'block', fontWeight: '700', fontSize: '0.76em', color: '#334155', marginBottom: '4px' }}>
                  แนบภาพถ่ายหลักฐาน
                </label>
                <DentalRiskUploadZone
                  images={images}
                  onChange={(updatedImages) => setImages(updatedImages)}
                  maxImages={5}
                />
              </div>

              {/* Patient */}
              <div>
                <label style={{ display: 'block', fontWeight: '700', fontSize: '0.76em', color: '#334155', marginBottom: '4px' }}>
                  ข้อมูลผู้ป่วย
                </label>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setIsPatientInvolved(!isPatientInvolved)}
                    style={{
                      padding: '5px 10px',
                      borderRadius: '6px',
                      border: isPatientInvolved ? '1.5px solid #0284c7' : '1px solid #cbd5e1',
                      backgroundColor: isPatientInvolved ? '#e0f2fe' : '#ffffff',
                      color: isPatientInvolved ? '#0369a1' : '#64748b',
                      fontSize: '0.76em',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    {isPatientInvolved ? '👤 มีผู้ป่วยเกี่ยวข้อง' : '❌ ไม่มีผู้ป่วย'}
                  </button>
                  {isPatientInvolved && (
                    <input
                      type="text"
                      placeholder="ระบุ HN ผู้ป่วย"
                      value={patientHn}
                      onChange={(e) => setPatientHn(e.target.value)}
                      style={{
                        flex: 1,
                        maxWidth: '180px',
                        padding: '5px 8px',
                        borderRadius: '6px',
                        border: '1.5px solid #0284c7',
                        fontSize: '0.78em',
                        backgroundColor: '#ffffff'
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Reporter Info (when not anonymous) */}
              {!isAnonymous && (
                <div>
                  <label style={{ display: 'block', fontWeight: '700', fontSize: '0.76em', color: '#334155', marginBottom: '4px' }}>
                    ข้อมูลผู้รายงาน
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '6px' }}>
                    <input
                      type="text"
                      placeholder="ชื่อ-สกุล ผู้รายงาน"
                      value={reporterName}
                      onChange={(e) => setReporterName(e.target.value)}
                      style={{
                        padding: '5px 8px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.78em',
                        backgroundColor: '#ffffff'
                      }}
                    />
                    <select
                      value={reporterRole}
                      onChange={(e) => setReporterRole(e.target.value)}
                      style={{
                        padding: '5px 8px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.78em',
                        backgroundColor: '#ffffff'
                      }}
                    >
                      <option value="ทันตแพทย์">ทันตแพทย์</option>
                      <option value="ทันตาภิบาล">ทันตาภิบาล</option>
                      <option value="ผู้ช่วยทันตแพทย์">ผู้ช่วยทันตแพทย์</option>
                      <option value="เจ้าหน้าที่งานจ่ายกลาง (CSSD)">เจ้าหน้าที่งานจ่ายกลาง (CSSD)</option>
                      <option value="พยาบาล/บุคลากรทางการแพทย์">พยาบาล/บุคลากรทางการแพทย์</option>
                      <option value="อื่นๆ">อื่นๆ</option>
                    </select>
                    <input
                      type="text"
                      placeholder="เบอร์โทร / Line"
                      value={contactInfo}
                      onChange={(e) => setContactInfo(e.target.value)}
                      style={{
                        padding: '5px 8px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.78em',
                        backgroundColor: '#ffffff'
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer: Anonymous Toggle & Submit Button */}
        <div
          style={{
            padding: '6px 10px',
            borderRadius: '8px',
            backgroundColor: isAnonymous ? '#fef2f2' : '#f8fafc',
            border: isAnonymous ? '1px solid #fca5a5' : '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '0.95em' }}>{isAnonymous ? '🔒' : '👤'}</span>
            <span style={{ fontWeight: '700', fontSize: '0.78em', color: isAnonymous ? '#991b1b' : '#334155' }}>
              {isAnonymous ? 'รายงานแบบนิรนาม (ไม่เปิดเผยตัวตน)' : '🔒 นิรนาม'}
            </span>
          </div>

          <label style={{ position: 'relative', display: 'inline-block', width: '38px', height: '20px' }}>
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span
              style={{
                position: 'absolute',
                cursor: 'pointer',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: isAnonymous ? '#ef4444' : '#cbd5e1',
                transition: '0.2s',
                borderRadius: '20px'
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  content: '""',
                  height: '14px',
                  width: '14px',
                  left: isAnonymous ? '20px' : '3px',
                  bottom: '3px',
                  backgroundColor: 'white',
                  transition: '0.2s',
                  borderRadius: '50%',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }}
              />
            </span>
          </label>
        </div>

        {submitError && (
          <div
            style={{
              padding: '6px 10px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              color: '#b91c1c',
              fontSize: '0.78em',
              fontWeight: '600'
            }}
          >
            ⚠️ {submitError}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            padding: '10px 16px',
            background: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '10px',
            fontSize: '0.94em',
            fontWeight: '800',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isSubmitting ? 0.75 : 1,
            boxShadow: '0 3px 10px rgba(5, 150, 105, 0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            transition: 'all 0.15s ease'
          }}
        >
          {isSubmitting ? (
            <>
              <span
                style={{
                  display: 'inline-block',
                  width: '14px',
                  height: '14px',
                  border: '2px solid #ffffff',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }}
              />
              <span>กำลังส่งรายงาน...</span>
            </>
          ) : (
            <>
              <span>⚡</span>
              <span>ส่งรายงานด่วน</span>
            </>
          )}
        </button>
      </form>

      {/* Camera Barcode Scanner Modal */}
      {showScanner && (
        <CameraScanner
          onScanSuccess={(decodedText) => handleBarcodeScanned(decodedText)}
          onClose={() => setShowScanner(false)}
          initialMode="single"
        />
      )}

      {/* Severity Guide Modal Popup */}
      <SeverityGuideModal
        isOpen={showSeverityGuide}
        onClose={() => setShowSeverityGuide(false)}
        initialTab={domain === 'non_clinical' ? 'non_clinical' : 'clinical'}
        selectedLevel={severityLevel}
        onSelectLevel={(code) => setSeverityLevel(code)}
      />
    </div>
  );
};

export default RiskQuickReportForm;
